import React from "react"
import { renderToStaticMarkup } from "react-dom/server"

import { svg } from "../../../Icon/img"
import type { MapLayer } from "../redux/global-map.types"

// The one colour per map layer that every Global Map surface shares — the KPI
// row, the quick-view header, the Nearby rows, the legend, the layer toggles
// and the map markers themselves. Lead blue, Project green, Subcontractor
// purple, Appointment cyan: the same four the KPI tiles have always used.
const ENTITY_ICON_COLOR: Record<MapLayer, string> = {
  project: "#16a34a",
  lead: "#1677ff",
  subcontractor: "#9333ea",
  appointment: "#0ea5e9"
}

// THE source of truth for "which glyph means a Lead / a Project": a location
// pin for a Lead, a house for a Project. Every surface resolves its glyph
// from here — the React `EntityIcon`/`EntityGlyphTile` components, the map
// legend, the layer toggles, and the native map marker below.
//
// This constant exists because the map marker and the legend had drifted onto
// different glyphs (a bullseye vs a pin, a briefcase vs a house), so the
// legend no longer explained the markers it sits next to.
const ENTITY_GLYPH: Record<MapLayer, keyof typeof svg> = {
  project: "Home",
  lead: "Map",
  subcontractor: "Projects",
  appointment: "Calendar"
}

// The marker content Google Maps renders is plain DOM built by
// `document.createElement` (`markers.ts`) — there is no React tree there to
// mount a component into. Rather than hand-copying the icon's path data into
// this file (which is exactly how the marker and the legend drifted apart in
// the first place), this renders the SAME icon component the React surfaces
// use, once per entity type, and caches the two resulting strings.
//
// `preserveAspectRatio` is explicit because the glyphs have different
// viewBoxes (the pin is tall, the house wide) and the marker draws them into
// one square box.
const markupCache = new Map<MapLayer, string>()

function entityIconSvgMarkup(entityType: MapLayer): string {
  const cached = markupCache.get(entityType)
  if (cached !== undefined) return cached

  const Glyph = svg[ENTITY_GLYPH[entityType]]
  const markup = renderToStaticMarkup(
    React.createElement(Glyph, {
      width: 18,
      height: 18,
      fill: "currentColor",
      preserveAspectRatio: "xMidYMid meet"
    })
  )
  markupCache.set(entityType, markup)
  return markup
}

export { ENTITY_ICON_COLOR, ENTITY_GLYPH, entityIconSvgMarkup }
